/**
 * 💰📊 Cassandra Ledger Snapshot Card — inline Kitchen widget.
 *
 * Compact card that shows the payoff of scanning receipts:
 *   • Grocery total spent (all time)
 *   • Receipt count
 *   • Per-category breakdown (top 5 chips)
 *   • 7-day daily-spend sparkline
 *   • Link to full Receipts History page
 *
 * Fed by GET /api/cassandra-ledger. Rerenders when `refreshKey`
 * (or the parent's own tick) changes.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  produce:   { icon: "🥦", color: "text-emerald-300" },
  protein:   { icon: "🥩", color: "text-red-300" },
  dairy:     { icon: "🥛", color: "text-sky-200" },
  grain:     { icon: "🌾", color: "text-amber-300" },
  pantry:    { icon: "🥫", color: "text-orange-300" },
  snack:     { icon: "🍿", color: "text-yellow-300" },
  beverage:  { icon: "🥤", color: "text-cyan-300" },
  household: { icon: "🧴", color: "text-fuchsia-300" },
  other:     { icon: "🛒", color: "text-neutral-300" },
};

function money(v) {
  const n = Number(v || 0);
  if (Number.isNaN(n)) return "$0.00";
  return new Intl.NumberFormat(undefined, {
    style: "currency", currency: "USD",
  }).format(n);
}

export default function LedgerSnapshotCard({ refreshKey = 0 }) {
  const [led, setLed] = useState(null);
  const [err, setErr] = useState(null);
  const [editingBudget, setEditingBudget] = useState(false);
  const [budgetDraft, setBudgetDraft] = useState("");
  const [savingBudget, setSavingBudget] = useState(false);

  const loadLedger = () => {
    axios.get(`${API}/cassandra-ledger`)
      .then((r) => { setLed(r.data); setErr(null); })
      .catch(() => setErr("Ledger unavailable."));
  };

  useEffect(() => { loadLedger(); }, [refreshKey]);

  const saveBudget = async () => {
    const val = parseFloat(budgetDraft);
    if (Number.isNaN(val) || val < 0) {
      toast.error("Enter a valid amount.");
      return;
    }
    setSavingBudget(true);
    try {
      const r = await axios.post(`${API}/cassandra-ledger/budget`, { monthly_budget: val });
      setLed(r.data);
      setEditingBudget(false);
      toast.success(val === 0 ? "Budget cleared." : `Monthly budget set to ${money(val)}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save budget.");
    } finally {
      setSavingBudget(false);
    }
  };

  if (err) {
    return (
      <div className="rounded-2xl border border-amber-500/30 bg-black/40 p-4 text-sm text-neutral-400"
           data-testid="ledger-snapshot-error">
        {err}
      </div>
    );
  }

  const scanned    = led?.receipts_scanned ?? 0;
  const total      = led?.grocery_total    ?? 0;
  const tax        = led?.grocery_tax      ?? 0;
  const byCat      = led?.by_category      ?? {};
  const trend      = led?.trend_7d         ?? [];
  const budget     = led?.monthly_budget   ?? 0;
  const monthSpend = led?.month_spend      ?? 0;
  const budgetPct  = led?.budget_pct;
  const remaining  = led?.budget_remaining;
  const catRows    = Object.entries(byCat)
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .slice(0, 5);
  const trendMax   = Math.max(1, ...trend.map((t) => Number(t.total) || 0));

  const empty = scanned === 0;

  return (
    <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-[#1a1208] via-black/60 to-[#1a0a05] p-4 mb-6 shadow-[0_0_20px_rgba(245,158,11,0.10)]"
         data-testid="ledger-snapshot-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="text-2xl">🧾</div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-amber-300/80 font-bold">
              Cassandra&apos;s Ledger
            </div>
            <div className="text-[10px] text-neutral-500">
              {empty ? "No receipts yet — scan one to start tracking." :
                       `${scanned} receipt${scanned === 1 ? "" : "s"} · running totals`}
            </div>
          </div>
        </div>
        <Link
          to="/receipts"
          className="text-[11px] px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-200 hover:bg-amber-500/20 transition"
          data-testid="ledger-snapshot-view-all"
        >
          View all →
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <SnapshotStat label="Total Spent"   value={money(total)}   accent="amber"
                      testid="ledger-stat-total" />
        <SnapshotStat label="Sales Tax"     value={money(tax)}
                      testid="ledger-stat-tax" />
        <SnapshotStat label="Receipts"      value={String(scanned)}
                      testid="ledger-stat-count" />
        <SnapshotStat label="Avg / Receipt" value={scanned ? money(total / scanned) : "—"}
                      testid="ledger-stat-avg" />
      </div>

      {/* 🎯 Monthly budget goal + progress bar */}
      <div className="rounded-lg bg-black/40 border border-neutral-800 px-3 py-2 mb-3"
           data-testid="ledger-budget-block">
        {editingBudget ? (
          <div className="flex items-center gap-2">
            <div className="text-[10px] uppercase tracking-wider text-neutral-400">Monthly Budget · USD</div>
            <input
              type="number"
              min="0"
              step="10"
              placeholder="e.g. 400"
              value={budgetDraft}
              onChange={(e) => setBudgetDraft(e.target.value)}
              autoFocus
              className="flex-1 px-2 py-1 rounded bg-black/60 border border-neutral-700 text-sm text-neutral-100 focus:border-amber-500/60 focus:outline-none"
              data-testid="ledger-budget-input"
            />
            <button
              onClick={saveBudget}
              disabled={savingBudget}
              className="text-[11px] px-2 py-1 rounded bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-200 font-bold disabled:opacity-60"
              data-testid="ledger-budget-save"
            >Save</button>
            <button
              onClick={() => setEditingBudget(false)}
              className="text-[11px] px-2 py-1 rounded bg-neutral-800 hover:bg-neutral-700 text-neutral-300"
              data-testid="ledger-budget-cancel"
            >Cancel</button>
          </div>
        ) : budget > 0 ? (
          <div>
            <div className="flex items-center justify-between text-[11px] mb-1">
              <div className="uppercase tracking-wider text-neutral-400">
                🎯 This Month · {money(monthSpend)} / {money(budget)}
              </div>
              <button
                onClick={() => { setBudgetDraft(String(budget)); setEditingBudget(true); }}
                className="text-[10px] text-amber-300 hover:text-amber-200 underline underline-offset-2"
                data-testid="ledger-budget-edit"
              >
                edit
              </button>
            </div>
            <BudgetProgressBar pct={budgetPct} remaining={remaining} />
          </div>
        ) : (
          <button
            onClick={() => { setBudgetDraft(""); setEditingBudget(true); }}
            className="w-full text-left text-xs text-amber-300 hover:text-amber-200 flex items-center gap-2"
            data-testid="ledger-budget-set"
          >
            🎯 <span className="underline underline-offset-2">Set a monthly grocery budget</span>
            <span className="text-neutral-500">— track over/under status</span>
          </button>
        )}
      </div>

      {catRows.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {catRows.map(([cat, amt]) => {
            const meta = CATEGORY_META[cat] || CATEGORY_META.other;
            return (
              <span
                key={cat}
                className={`text-[11px] px-2 py-0.5 rounded-full bg-black/50 border border-neutral-800 ${meta.color}`}
                data-testid={`ledger-cat-${cat}`}
              >
                {meta.icon} {cat} · {money(amt)}
              </span>
            );
          })}
        </div>
      )}

      {trend.length > 0 && (
        <div className="rounded-lg bg-black/40 border border-neutral-800 p-2">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">
            Last 7 days
          </div>
          <div className="flex items-end gap-1 h-14" data-testid="ledger-sparkline">
            {trend.map((t) => {
              const h = Math.max(3, Math.round((Number(t.total) / trendMax) * 100));
              const day = new Date(t.date + "T00:00:00");
              const dow = day.toLocaleDateString(undefined, { weekday: "narrow" });
              return (
                <div key={t.date} className="flex-1 flex flex-col items-center gap-0.5"
                     title={`${t.date}: ${money(t.total)}`}>
                  <div
                    className={`w-full rounded-t ${Number(t.total) > 0 ? "bg-gradient-to-t from-amber-600 to-amber-300" : "bg-neutral-800"}`}
                    style={{ height: `${h}%` }}
                  />
                  <div className="text-[9px] text-neutral-500">{dow}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SnapshotStat({ label, value, accent, testid }) {
  return (
    <div className="rounded-lg bg-black/40 border border-neutral-800 px-3 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className={`text-lg font-black ${accent === "amber" ? "text-amber-300" : "text-neutral-100"}`}
           style={{ fontFamily: "Anton, sans-serif" }}>
        {value}
      </div>
    </div>
  );
}

function BudgetProgressBar({ pct, remaining }) {
  const clamped = Math.max(0, Math.min(100, pct ?? 0));
  const over = (pct ?? 0) > 100;
  const barGrad = over
    ? "from-red-500 to-red-700"
    : (pct ?? 0) >= 80
      ? "from-amber-500 to-orange-600"
      : "from-emerald-500 to-emerald-700";
  return (
    <div>
      <div className="h-2.5 rounded-full bg-black/60 border border-neutral-800 overflow-hidden relative">
        <div
          className={`h-full bg-gradient-to-r ${barGrad} transition-all duration-500`}
          style={{ width: `${clamped}%` }}
          data-testid="ledger-budget-progress-fill"
        />
        {over && (
          <div className="absolute inset-0 flex items-center justify-center text-[9px] font-black text-white tracking-wider">
            OVER
          </div>
        )}
      </div>
      <div className="flex items-center justify-between text-[10px] mt-1">
        <span className={over ? "text-red-300" : "text-neutral-400"}>
          {pct != null ? `${pct}% used` : ""}
        </span>
        <span className={over ? "text-red-300 font-bold" : "text-emerald-300"}>
          {remaining != null
            ? (over ? `${money(-remaining)} over` : `${money(remaining)} left`)
            : ""}
        </span>
      </div>
    </div>
  );
}
